import type { Locale } from '@/intl/types'
import type groupResponsibleAPIClient from '@/scopes/account/group-responsible/infrastructure/groupResponsibleAPIClient'
import type { groupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/store'
import type QsClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'

export interface GroupResponsibleUseCases {
  apiClient: typeof groupResponsibleAPIClient
  store: typeof groupResponsibleStore
  queryStringClient: typeof QsClient
  locale?: Locale
}

function groupResponsibleUseCases({
  apiClient,
  store,
  queryStringClient,
  locale,
}: GroupResponsibleUseCases) {
  async function pageLoaded() {
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    let data
    const state = store.getState()
    state.setIn('retrieveBookingData.loading', true)
    try {
      data = await apiClient.getGroupBookingData(masterBookingNumber || '', locale)
      if (!data) {
        state.setIn('retrieveBookingData', { loading: false, error: true })
        return
      }
    }
    catch {
      state.setIn('retrieveBookingData', { loading: false, error: true })
      return
    }
    state.setIn('retrieveBookingData.loading', false)

    state.initialize({
      bookingData: {
        group: {
          name: data?.group.name ?? '',
          imgSrc: data?.group.imgSrc ?? '',
          location: data?.group.location ?? '',
          period: data?.group.period ?? '',
          startingDate: data?.group.startingDate ?? '',
        },
        labels: data?.labels,
      },
      participants: data?.participants,
      rooms: data?.rooms,
      buses: data?.buses,
    })
  }

  async function assignBusToParticipants() {
    const state = store.getState()
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    let { selectedPickupPointId, selectedReturnPointId, sameAsPickup } = state.busAssignment

    if (!selectedPickupPointId && state.busPickupPoints.length === 1) {
      selectedPickupPointId = state.busPickupPoints[0].id
      state.setIn('busAssignment.selectedPickupPointId', selectedPickupPointId)
    }

    if (!selectedReturnPointId && state.busReturnPoints.length === 1) {
      selectedReturnPointId = state.busReturnPoints[0].id
      state.setIn('busAssignment.selectedReturnPointId', selectedReturnPointId)
    }

    const finalReturnId = sameAsPickup ? selectedPickupPointId : selectedReturnPointId

    const pickupError = state.busPickupPoints.length > 1 && !selectedPickupPointId
    const returnError = !sameAsPickup && state.busReturnPoints.length > 1 && !selectedReturnPointId

    state.setIn('busAssignment.pickupPointNotSelected', pickupError)
    state.setIn('busAssignment.returnPointNotSelected', returnError)

    if (pickupError || returnError) {
      return
    }

    state.setIn('busAssignment.submitting', true)
    state.setIn('busAssignment.error', false)

    try {
      const participantIds = Array.from(state.selectedParticipantIds)

      await apiClient.assignBusToParticipants({
        masterBookingNumber: masterBookingNumber || '',
        participantIds,
        pickupPointId: selectedPickupPointId,
        returnPointId: finalReturnId,
      })

      participantIds.forEach((id) => {
        state.setIn(`participants.${id}.isBusAssignmentProcessing`, true)
      })
      state.setIn('selectedParticipantIds', new Set())
      state.setIn('busAssignment', {
        selectedPickupPointId: '',
        selectedReturnPointId: '',
        sameAsPickup: true,
        submitting: false,
        error: false,
        pickupPointNotSelected: false,
        returnPointNotSelected: false,
      })

      state.toggleIsEditingParticipants(false)
    }
    catch {
      state.setIn('busAssignment.error', true)
      state.setIn('busAssignment.submitting', false)
    }
  }

  return {
    pageLoaded,
    assignBusToParticipants,
  }
}

export default groupResponsibleUseCases
